import { Receipt } from 'lucide-react';

import type { GuideModule } from '../types';

/**
 * `requiredPermission: 'invoice.read'` matches the `NavPermission` that
 * gates "Facturación" in `layout/navigation.ts`. Re-verified in PR 13
 * against the real screens (`invoice-list-page.tsx`,
 * `invoice-detail-page.tsx`): every quoted button label ("Generar
 * borradores", "Excluir", "Emitir", "Volver a borrador", "Reconciliar",
 * "Descargar PDF") and status label ("Borrador", "Enviada a ARCA",
 * "Emitida", "Rechazada") below matches exactly — same discipline
 * `delivery-notes.ts`'s own labels already follow against real buttons.
 */
export const BILLING_MODULE: GuideModule = {
  id: 'billing',
  title: 'Facturación',
  icon: Receipt,
  requiredPermission: 'invoice.read',
  priority: 10,
  summary:
    'Una factura se genera agrupando los remitos firmados de un cliente y un contrato dentro de un mismo período de facturación, nunca mezclando clientes ni períodos distintos. El borrador queda editable hasta que lo emitís: podés excluir un remito con un motivo si algo no corresponde todavía. Al emitir, el sistema obtiene el CAE (el código de autorización fiscal) y la factura queda inmutable, con su PDF descargable. Mientras el ambiente fiscal activo no sea Producción (ver "Configuración fiscal"), lo vas a ver como un aviso permanente en el listado, en los borradores y en las facturas emitidas: "COMPROBANTE DE PRUEBA — SIN VALIDEZ FISCAL (SIMULADO)" mientras el ambiente sea Simulado, u "... (HOMOLOGACIÓN)" si ya se configuró el certificado de Homologación; en Producción ese aviso desaparece.',
  steps: [
    {
      title: 'Generar el borrador de un período',
      description:
        'Desde "Generar borradores", completá "Período desde" y "Período hasta" y confirmá. El sistema agrupa los remitos firmados de ese rango por cliente, contrato y ciclo de facturación, y crea un borrador por cada grupo. Un borrador todavía no tiene número: lo vas a ver como "sin número" hasta que se emita.',
      keyTerms: ['borrador-de-factura', 'remito-firmado'],
    },
    {
      title: 'Revisar el detalle y excluir un remito si hace falta',
      description:
        'Entrá al detalle del borrador para ver cada ítem con su sede, cantidad y precio, y el total por alícuota de IVA. Si algo no corresponde a esa factura, hacé clic en "Excluir" sobre el ítem e indicá el motivo: se quita el remito completo al que pertenece, que vuelve a estar disponible para un período posterior, y los totales se recalculan.',
      keyTerms: ['punto-de-venta'],
    },
    {
      title: 'Saber qué concepto se envía a ARCA',
      description:
        'Cada ítem del borrador toma el concepto de facturación del precio del contrato del servicio (almuerzo o cena) con el que se valorizó; un plato con excepción por plato se factura siempre bajo el concepto por defecto. El Concepto que se informa a ARCA se deduce de esos ítems: si todos son de tipo Producto, se envía 1 (Productos); si todos son de tipo Servicio, 2 (Servicios); si hay de ambos tipos, 3 (Productos y Servicios). Al excluir un remito, el sistema recalcula el Concepto con los ítems que quedan. Los conceptos se administran en Configuración → Conceptos de Facturación y se asignan a los precios desde la lista de precios de cada contrato.',
      keyTerms: ['borrador-de-factura'],
    },
    {
      title: 'Emitir la factura',
      description:
        'Con el borrador revisado, hacé clic en "Emitir". El sistema reserva el número correlativo del punto de venta y solicita el CAE. Si el organismo lo autoriza, la factura queda "Emitida" e inmutable; si la rechaza, queda "Rechazada" con el detalle del motivo.',
      keyTerms: ['cae', 'factura'],
    },
    {
      title: 'Emitir varias facturas a la vez',
      description:
        'En el listado, marcá los borradores que quieras emitir y hacé clic en "Emitir seleccionadas". Se emiten de a una, en orden, y al terminar ves el resultado de cada factura: si una falla, las demás siguen.',
      keyTerms: ['factura'],
    },
    {
      title: 'Corregir una factura rechazada',
      description:
        'Sobre una factura "Rechazada", hacé clic en "Volver a borrador" para poder corregirla (por ejemplo, datos fiscales del cliente) y emitirla de nuevo. Una factura rechazada nunca se emite directamente: siempre pasa primero por este paso.',
      keyTerms: ['factura'],
    },
    {
      title: 'Reconciliar una factura atascada',
      description:
        'Si una factura queda "Enviada a ARCA" por mucho tiempo sin resolverse (por ejemplo, si el proceso se interrumpió), hacé clic en "Reconciliar": el sistema consulta si el organismo ya la autorizó y actualiza el estado en consecuencia, sin que tengas que reintentar a ciegas.',
      keyTerms: ['reconciliar'],
    },
    {
      title: 'Descargar el PDF de una factura emitida',
      description:
        'Desde el detalle de una factura ya emitida (aunque ya tenga cobros o esté vencida), hacé clic en "Descargar PDF" para obtener el comprobante completo, con el código QR normativo y el CAE. Mientras el comprobante sea de prueba (ambiente Simulado u Homologación), el PDF incluye la misma marca genérica "COMPROBANTE DE PRUEBA — SIN VALIDEZ FISCAL", sin distinguir el ambiente en el propio archivo.',
      keyTerms: ['comprobante-de-prueba'],
    },
  ],
  states: [
    {
      value: 'DRAFT',
      label: 'Borrador',
      description: 'Generada, todavía editable: se pueden excluir remitos antes de emitir.',
    },
    {
      value: 'PENDING_ARCA',
      label: 'Enviada a ARCA',
      description:
        'Esperando la respuesta del organismo fiscal. Si tarda demasiado, se puede reconciliar.',
    },
    {
      value: 'ISSUED',
      label: 'Emitida',
      description: 'Tiene CAE. Inmutable: no admite edición ni exclusión de remitos.',
    },
    {
      value: 'REJECTED',
      label: 'Rechazada',
      description:
        'El organismo fiscal la rechazó. Hay que volverla a borrador antes de poder emitirla de nuevo.',
    },
  ],
  faq: [
    {
      question: '¿Por qué mi factura tiene la marca "COMPROBANTE DE PRUEBA"?',
      answer:
        'Porque el ambiente fiscal activo todavía no es Producción. En Simulado el CAE es simulado y no hay conexión real al organismo; en Homologación ya hay conexión real, pero contra el ambiente de pruebas de ARCA, sin validez fiscal. Esa marca desaparece recién cuando, desde "Configuración fiscal", se pasa el ambiente a Producción.',
    },
    {
      question: '¿Por qué un borrador dice "sin número"?',
      answer:
        'Porque el número de factura lo asigna la emisión: se toma el siguiente correlativo del punto de venta recién cuando ARCA autoriza el comprobante. Así nunca quedan huecos en la numeración.',
    },
    {
      question: '¿Dónde configuro el concepto o el plazo de pago que se envía a ARCA?',
      answer:
        'Ya no hay un parámetro global de facturación: el Concepto lo determinan los conceptos asignados a cada precio del contrato, y el vencimiento del pago sale del plazo de pago de cada contrato.',
    },
    {
      question: '¿Puedo emitir una factura rechazada directamente?',
      answer:
        'No. Primero tenés que volverla a borrador, corregir lo que haya causado el rechazo, y recién ahí emitirla de nuevo.',
    },
    {
      question: '¿Qué pasa con un remito que excluyo de una factura?',
      answer:
        'Vuelve a estar disponible sin factura asignada, listo para incluirse en el borrador de un período posterior.',
    },
    {
      question: '¿Por qué una factura queda "Enviada a ARCA" sin resolverse?',
      answer:
        'Puede pasar si el proceso que la estaba emitiendo se interrumpió antes de recibir la respuesta del organismo. Usá "Reconciliar" para que el sistema consulte el resultado real en vez de dejarla así indefinidamente.',
    },
  ],
  tips: [
    'Revisá el detalle del borrador antes de emitir: una vez emitida, la factura es inmutable y cualquier corrección pasa por una nota de crédito (todavía no disponible en este módulo).',
    'El número de una factura rechazada nunca se pierde: se reasigna automáticamente a la siguiente emisión exitosa de ese mismo punto de venta y tipo de comprobante.',
  ],
};
